import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation, Link, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import { useData } from '../context/DataContext';
import ImageViewer from '../components/ImageViewer';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiExternalLink, FiStar, FiHash, FiTag, FiZoomIn } = FiIcons;

const ProductDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { products } = useData();
  const [showImageViewer, setShowImageViewer] = useState(false);
  
  // Get product from state or find it by ID/SKU
  const product = useMemo(() => {
    if (location.state?.product) {
      return location.state.product;
    }
    
    // Extract ID from URL params
    const productId = params.productId;
    if (!productId) return null;
    
    // Try to find product by ID first
    let foundProduct = products.find(p => p.id === productId);
    
    // If not found by ID, try by SKU
    if (!foundProduct) {
      foundProduct = products.find(p => p.sku && p.sku.toLowerCase() === productId.toLowerCase());
    }
    
    return foundProduct;
  }, [location.state, params, products]);
  
  // Find similar products (same category)
  const similarProducts = useMemo(() => {
    if (!product || !products || products.length === 0) return [];
    
    // Get product categories
    const productCategories = product.categories ? 
      product.categories.split(',').map(cat => cat.trim()) : [];
    
    if (productCategories.length === 0) return [];
    
    // Find products with at least one matching category
    return products
      .filter(p => 
        p.id !== product.id && // Not the same product
        p.categories && // Has categories
        productCategories.some(cat => 
          p.categories.split(',').map(c => c.trim()).includes(cat)
        )
      )
      .slice(0, 4); // Limit to 4 similar products
  }, [product, products]);

  // Handle category click
  const handleCategoryClick = (category) => {
    navigate('/', { state: { selectedCategory: category.trim() } });
  };

  // Fix for image viewer - reset on component unmount
  useEffect(() => {
    return () => {
      // Cleanup function
      document.body.style.overflow = 'visible';
    };
  }, []);

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Product not found</h2>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  // Image viewer handlers with debugging
  const handleImageClick = () => {
    console.log('Image clicked, opening viewer');
    setShowImageViewer(true);
    // Lock body scroll when viewer is open
    document.body.style.overflow = 'hidden';
  };

  const handleCloseViewer = () => {
    console.log('Closing viewer');
    setShowImageViewer(false);
    // Restore body scroll when viewer is closed
    document.body.style.overflow = 'visible';
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-3 md:py-4">
            {/* Desktop Header */}
            <div className="hidden md:flex items-center justify-between">
              <div className="flex items-center">
                <Link to="/" className="text-2xl font-bold text-[#8d2424] hover:text-[#7a1e1e] transition-colors no-underline">
                  LiveWire Product Search Database
                </Link>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/')}
                  className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <SafeIcon icon={FiArrowLeft} className="text-lg" />
                  <span>Back to Search</span>
                </button>
              </div>
            </div>
            
            {/* Mobile Header */}
            <div className="md:hidden">
              <div className="flex items-center justify-between mb-2">
                <Link to="/" className="text-xl font-bold text-[#8d2424] text-left hover:text-[#7a1e1e] transition-colors no-underline">
                  LiveWire Product Search Database
                </Link>
              </div>
              <div className="flex items-center justify-start mt-2">
                <button
                  onClick={() => navigate('/')}
                  className="flex items-center space-x-1 px-2 py-1.5 text-white bg-[#8d2424] rounded hover:bg-[#7a1e1e] transition-colors text-xs"
                >
                  <SafeIcon icon={FiArrowLeft} className="text-lg text-white" />
                  <span>Back to Search</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-grow">
        <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
          >
            {/* Product Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex flex-col lg:flex-row lg:items-start lg:space-x-8">
                {product.logo && (
                  <div className="flex-shrink-0 mb-6 lg:mb-0 flex justify-center lg:justify-start">
                    <div 
                      className="relative group cursor-pointer"
                      onClick={handleImageClick}
                    >
                      <img
                        src={product.logo}
                        alt={product.name}
                        className="w-60 h-60 sm:w-72 sm:h-72 lg:w-[30rem] lg:h-[30rem] object-contain rounded-lg border border-gray-200"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-lg">
                        <div className="bg-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <SafeIcon icon={FiZoomIn} className="text-xl text-[#8d2424]" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
                    <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
                      {product.name}
                    </h1>
                    {product.featured && (
                      <div className="flex items-center space-x-1 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                        <SafeIcon icon={FiStar} className="text-sm" />
                        <span>Featured</span>
                      </div>
                    )}
                  </div>

                  {product.tagline && (
                    <p className="text-lg lg:text-xl font-bold text-[#8d2424] mb-4 break-words">
                      {product.tagline}
                    </p>
                  )}

                  {/* SKU (if available) */}
                  {product.sku && (
                    <div className="flex items-center space-x-2 mb-4">
                      <SafeIcon icon={FiHash} className="text-gray-400" />
                      <span className="text-sm font-medium text-gray-600">SKU: {product.sku}</span>
                    </div>
                  )}

                  {/* Categories - now clickable and left-aligned */}
                  {product.categories && (
                    <div className="flex flex-wrap items-center gap-2 mb-6">
                      <SafeIcon icon={FiTag} className="text-gray-400" />
                      <div className="flex flex-wrap gap-2">
                        {product.categories.split(',').map((category, index) => (
                          <button
                            key={index}
                            onClick={() => handleCategoryClick(category)}
                            className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm font-medium border border-black hover:bg-gray-200 transition-colors text-left"
                          >
                            {category.trim()}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* External Link */}
                  {product.url && (
                    <a
                      href={product.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      <span>View Product</span>
                      <SafeIcon icon={FiExternalLink} className="text-sm" />
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Product Content Link - Removed as per instructions */}
          </motion.div>

          {/* Similar Products Section */}
          {similarProducts.length > 0 && (
            <div className="mt-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Similar Products</h2>
              <motion.div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                {similarProducts.map((similarProduct, index) => (
                  <motion.div
                    key={similarProduct.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 + index * 0.1, duration: 0.3 }}
                  >
                    <ProductCard
                      product={similarProduct}
                      onClick={() => {
                        navigate(`/product/${similarProduct.id}`, { state: { product: similarProduct } });
                        window.scrollTo(0, 0);
                      }}
                      onCategoryClick={handleCategoryClick}
                    />
                  </motion.div>
                ))}
              </motion.div>
            </div>
          )}
        </div>
      </div>

      {/* Image Viewer Modal */}
      <AnimatePresence>
        {showImageViewer && product.logo && (
          <ImageViewer 
            imageUrl={product.logo} 
            alt={product.name} 
            onClose={handleCloseViewer} 
          />
        )}
      </AnimatePresence>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ProductDetailPage;